import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sparkles, Upload, Type, Download, Share2, ArrowRight, Zap, Shield, Palette } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const [templateCount, setTemplateCount] = useState(25);
  
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const response = await axios.get(`${API}/templates/count`);
        setTemplateCount(response.data.count);
      } catch (error) {
        // Template count is non-critical, silently fail
      }
    };
    fetchCount();
  }, []);

  const features = [
    {
      icon: Upload,
      title: 'Upload or Choose Template',
      description: 'Start with your own image or pick from our collection of popular meme templates.'
    },
    {
      icon: Type,
      title: 'Advanced Text Editor',
      description: 'Multiple fonts, colors, outlines, shadows, and precise positioning for perfect memes.'
    },
    {
      icon: Download,
      title: 'Export in HD',
      description: 'Download your meme in high-quality PNG or JPG format instantly.'
    },
    {
      icon: Share2,
      title: 'Share Everywhere',
      description: 'Share directly to Twitter, Facebook, Reddit, or copy the link.'
    }
  ];

  const stats = [
    { value: `${templateCount}+`, label: 'Templates' },
    { value: '10K+', label: 'Memes Created' },
    { value: '100%', label: 'Free' },
  ];

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#09090b]">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#bef264]/5 to-transparent" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-[#bef264]/10 rounded-full blur-[120px] opacity-30" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 sm:pt-24 sm:pb-32">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#bef264]/10 border border-[#bef264]/20 rounded-full mb-8 animate-fade-in">
              <Zap className="w-4 h-4 text-[#bef264]" />
              <span className="text-sm font-medium text-[#bef264]">Free & No Sign-up Required</span>
            </div>

            {/* Main Heading */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 font-['Space_Grotesk'] tracking-tight leading-none animate-fade-in" style={{ animationDelay: '0.1s' }}>
              Create Epic Memes
              <br />
              <span className="text-[#bef264]">In Seconds</span>
            </h1>

            <p className="text-lg sm:text-xl text-[#a1a1aa] max-w-2xl mx-auto mb-10 animate-fade-in" style={{ animationDelay: '0.2s' }}>
              The most powerful meme editor on the web. No watermarks, no limits.
              Upload your image or choose a template and start creating.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <Link to="/editor">
                <Button 
                  size="lg" 
                  className="bg-[#bef264] text-black hover:bg-[#a3e635] font-semibold text-lg px-8 h-14 glow-primary hover:glow-primary-strong transition-all"
                  data-testid="start-creating-btn"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Start Creating
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/templates">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-[#27272a] text-white hover:bg-white/5 font-medium text-lg px-8 h-14"
                  data-testid="browse-templates-btn"
                >
                  Browse Templates
                </Button>
              </Link>
            </div>

            {/* Stats */}
            <div className="flex items-center justify-center gap-8 sm:gap-16 mt-16 pt-8 border-t border-[#27272a] animate-fade-in" style={{ animationDelay: '0.4s' }}>
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl sm:text-4xl font-bold text-white font-['Space_Grotesk']">{stat.value}</div>
                  <div className="text-sm text-[#a1a1aa] mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 sm:py-28 border-t border-[#27272a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-['Space_Grotesk']">
              Everything You Need
            </h2>
            <p className="text-[#a1a1aa] text-lg max-w-2xl mx-auto">
              Professional meme creation tools that are actually free and easy to use.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, idx) => (
              <div
                key={feature.title}
                className="group p-6 bg-[#18181b] border border-[#27272a] rounded-xl hover:border-[#bef264]/30 transition-all duration-300"
                style={{ animationDelay: `${idx * 0.1}s` }}
              >
                <div className="w-12 h-12 bg-[#bef264]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#bef264]/20 transition-colors">
                  <feature.icon className="w-6 h-6 text-[#bef264]" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-[#a1a1aa] text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 border-t border-[#27272a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#22d3ee]/10 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-[#22d3ee]" />
              </div>
              <div>
                <div className="text-white font-medium">No Watermarks</div>
                <div className="text-[#a1a1aa] text-sm">100% clean exports</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#c084fc]/10 rounded-lg flex items-center justify-center">
                <Zap className="w-5 h-5 text-[#c084fc]" />
              </div>
              <div>
                <div className="text-white font-medium">Lightning Fast</div>
                <div className="text-[#a1a1aa] text-sm">No loading screens</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#bef264]/10 rounded-lg flex items-center justify-center">
                <Palette className="w-5 h-5 text-[#bef264]" />
              </div>
              <div>
                <div className="text-white font-medium">Full Customization</div>
                <div className="text-[#a1a1aa] text-sm">Advanced text tools</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 sm:py-28 border-t border-[#27272a]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 font-['Space_Grotesk']">
            Ready to Create Your Meme?
          </h2>
          <p className="text-[#a1a1aa] text-lg mb-10">
            Join thousands of creators making viral content every day.
          </p>
          <Link to="/editor">
            <Button 
              size="lg" 
              className="bg-[#bef264] text-black hover:bg-[#a3e635] font-semibold text-lg px-10 h-14 glow-primary"
              data-testid="cta-start-btn"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Get Started - It's Free
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-[#27272a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-[#bef264] rounded-md flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-black" />
              </div>
              <span className="text-sm font-semibold text-white">MemeStudio</span>
            </div>
            <p className="text-sm text-[#a1a1aa]">
              Made with ❤️ for meme creators everywhere
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
